import type { searchDataType } from '~/storyblok/ProgrammeSchedule.vue';

export function updateUrlParams(params: Record<string, string | null>, replaceState = false) {
  if (!import.meta.client) return;

  const url = new URL(window.location.href);

  // Update or remove each parameter
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === '') {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value);
    }
  });

  // Update URL without full page reload
  if (replaceState) {
    window.history.replaceState({}, '', url.toString());
  } else {
    window.history.pushState({}, '', url.toString());
  }
}

/**
 * Parse URL parameters into filter data
 */
export function parseUrlFilters() {
  if (!import.meta.client)
    return {
      term: '',
      filters: [],
      dateFilters: [],
    };

  try {
    const url = new URL(window.location.href);

    const term = url.searchParams.get('q') || '';

    const categoryParam = url.searchParams.get('category') || '';
    const arenaParam = url.searchParams.get('arena') || '';

    const date = url.searchParams.get('date') || '';
    const dateFilters = date
      ? date
          .split(',')
          .map((d) => {
            try {
              const [day, month] = d.split('-');
              const year = new Date().getFullYear();
              return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            } catch (e) {
              console.warn('Invalid date format in URL:', d);
              return '';
            }
          })
          .filter(Boolean)
      : [];

    return {
      term,
      categorySlugParam: categoryParam,
      arenaSlugParam: arenaParam,
      // Keep filters empty - will be populated after categories are loaded
      filters: [],
      dateFilters,
    };
  } catch (error) {
    console.error('Error parsing URL filters:', error);
    return {
      term: '',
      categorySlugParam: '',
      arenaSlugParam: '',
      filters: [],
      dateFilters: [],
    };
  }
}

/**
 * Convert filter data to URL parameters
 */
export function filtersToUrlParams(searchData: searchDataType, filterGroups: any[]) {
  try {
    const arenaGroup = filterGroups.find(
      (group) => group.heading.includes('Venue') || group.heading.includes('Arena'),
    );

    const categoryGroup = filterGroups.find(
      (group) => group.heading.includes('Categories') || group.heading.includes('Kategori'),
    );

    const arenaIds = arenaGroup
      ? searchData.filters.filter((id) =>
          arenaGroup.options.some((option: any) => option.id === id),
        )
      : [];

    const categoryIds = categoryGroup
      ? searchData.filters.filter((id) =>
          categoryGroup.options.some((option: any) => option.id === id),
        )
      : [];

    // Get option values (slugs) instead of IDs for URL
    const arenaValues = arenaIds
      .map((id) => {
        const option = arenaGroup?.options.find((o) => o.id === id);
        return option?.value || '';
      })
      .filter(Boolean);

    const categoryValues = categoryIds
      .map((id) => {
        const option = categoryGroup?.options.find((o) => o.id === id);
        return option?.value || '';
      })
      .filter(Boolean);

    // Format dates from YYYY-MM-DD to DD-M
    const dateValues = searchData.dateFilters
      .map((date) => {
        try {
          const [year, month, day] = date.split('-');
          return `${day}-${month}`;
        } catch (e) {
          console.warn('Invalid date format in dateFilters:', date);
          return '';
        }
      })
      .filter(Boolean);

    interface UrlParams {
      q: string | null;
      arena: string | null;
      category: string | null;
      date: string | null;
    }

    const params: UrlParams = {
      q: null,
      arena: null,
      category: null,
      date: null,
    };

    if (searchData.term) {
      params.q = searchData.term;
    } else {
      params.q = null;
    }

    if (arenaValues.length) {
      params.arena = arenaValues.join(',');
    } else {
      params.arena = null;
    }

    if (categoryValues.length) {
      params.category = categoryValues.join(',');
    } else {
      params.category = null;
    }

    if (dateValues.length) {
      params.date = dateValues.join(',');
    } else {
      params.date = null;
    }

    return params;
  } catch (error) {
    console.error('Error converting filters to URL params:', error);
    return {};
  }
}

/**
 * Find filter IDs by slug values
 */
export function findFilterIdsByValues(values: string[], filterGroups: any[]) {
  try {
    if (!Array.isArray(values) || !Array.isArray(filterGroups)) {
      return [];
    }

    const ids: string[] = [];

    values.forEach((value) => {
      if (!value) return;

      filterGroups.forEach((group) => {
        if (!group || !Array.isArray(group.options)) return;

        const option = group.options.find((o) => o && o.value === value);
        if (option && option.id) {
          ids.push(option.id);
        }
      });
    });

    return ids;
  } catch (error) {
    console.error('Error finding filter IDs by values:', error);
    return [];
  }
}
